import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { fonts } from "../../../styles/theme";
import { resumoDasBuscas } from "../../dashboard/components/search-terms-growth/search-terms-summary";
import type { IDashboardData, ISearchTermsGrowth } from "../../dashboard/types";
import { Cabecalho, Capa, Carregando, EmPreparacao, NaoEncontrado, Pe } from "../componentes";
import { Imagem } from "../componentes/imagem";
import { NumeroAnimado } from "../componentes/numero-animado";
import { resumoDasPosicoes, ultimaMedicao } from "../dados/buscas";
import { LinhaDeBuscaItem } from "../componentes/linha-de-busca";
import { dataCurta, mesCurto, numero, primeiraFrase } from "../dados/formatar";
import { PERIODOS, seloDeVariacao, variacaoDoPeriodo, type CategoriaDoPeriodo } from "../dados/periodo";
import {
  avaliacaoEmDestaque,
  estrelas,
  impulsionamentosDoPeriodo,
  linhaDoTempo,
  respostasDoPeriodo,
} from "../dados/trabalho";
import { maiorAvanco } from "../dados/winback";
import { emPreparacao, useRelatorio } from "../hooks/use-relatorio";
import {
  Apoio,
  Aviso,
  Barra,
  Bloco,
  Card,
  Centro,
  Chip,
  Chips,
  Destaque,
  DuasColunas,
  Etiqueta,
  Faixa,
  Grade,
  Largura,
  Meta,
  Numero,
  Pagina,
  Secao,
  Titulo,
  cores,
} from "../styles";

const Cabeca = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (min-width: 900px) {
    gap: 8px;
  }
`;

const CabecaComAcao = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 900px) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    gap: 24px;
  }
`;

const Coluna = styled.div<{ $gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap ?? 16}px;
`;

const Kpi = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;

  @media (min-width: 900px) {
    gap: 14px;
    padding: 28px;
  }
`;

const Rotulo = styled(Meta)`
  font-family: ${fonts.gilroySemiBold}, sans-serif;
  color: ${cores.ink3};
  @media (min-width: 900px) {
    font-size: 14px;
  }
`;

const LinkSimples = styled(Link)`
  font-family: ${fonts.gilroySemiBold}, sans-serif;
  font-size: 14px;
  @media (min-width: 900px) {
    font-size: 15px;
  }
`;

const LinkExterno = styled.a`
  font-family: ${fonts.gilroySemiBold}, sans-serif;
  font-size: 14px;
  @media (min-width: 900px) {
    font-size: 15px;
  }
`;

const Seta = ({ cor = cores.green, tamanho = 16 }: { cor?: string; tamanho?: number }) => (
  <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none" stroke={cor} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const ordinal = (posicao: number) => `${posicao}º`;

const NomeNoMercado = styled.span<{ $destaque?: boolean }>`
  width: 110px;
  flex-shrink: 0;
  font-size: 13px;
  line-height: 1.25;
  font-family: ${({ $destaque }) => ($destaque ? fonts.gilroySemiBold : fonts.gilroyRegular)}, sans-serif;
  color: ${({ $destaque }) => ($destaque ? cores.ink : cores.ink3)};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  @media (min-width: 900px) {
    width: 170px;
    font-size: 14px;
  }
`;

const LinhaDeMercado = ({ nome, valor, total, destaque, atraso }: { nome: string; valor: number; total: number; destaque?: boolean; atraso: number }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
    <NomeNoMercado $destaque={destaque} title={nome}>
      {nome}
    </NomeNoMercado>
    <div style={{ flex: 1 }}>
      <Barra $largura={total ? (valor / total) * 100 : 0} $atraso={atraso} $cor={destaque ? cores.blue : "#C2D4F0"} style={{ height: 8, borderRadius: 4 }} />
    </div>
    <Meta style={{ width: 40, textAlign: "right", fontFamily: fonts.gilroyBold, color: destaque ? cores.ink : cores.ink4 }}>{`${valor}/${total}`}</Meta>
  </div>
);

// As quatro cores do Google, na ordem do logo (azul, vermelho, amarelo, verde),
// no tom que o Google usa para texto sobre fundo pastel; o selo de crescimento
// continua verde em todos.
const KPIS: { chave: keyof IDashboardData["analytics"]; categoria: CategoriaDoPeriodo; rotulo: string; fundo: string; cor: string }[] = [
  { chave: "callClicks", categoria: "CALLS", rotulo: "Ligações", fundo: "#E8F0FE", cor: "#1A73E8" },
  { chave: "requestedRoutes", categoria: "DIRECTIONS", rotulo: "Pedidos de rota", fundo: "#FCE8E6", cor: "#D93025" },
  { chave: "websiteClicks", categoria: "WEBSITE", rotulo: "Acessos ao site", fundo: "#FEF7E0", cor: "#E37400" },
  { chave: "viewsInMap", categoria: "VIEWS", rotulo: "Aparições no Maps", fundo: "#E6F4EA", cor: "#188038" },
];

const analyticsValidos = (analytics: IDashboardData["analytics"] | null | undefined) =>
  analytics && KPIS.some((kpi) => (analytics[kpi.chave] ?? 0) > 0) ? analytics : null;

const termosMaisBuscados = (dados: ISearchTermsGrowth, quantos = 3) =>
  dados.terms
    .map((termo) => ({
      termo: termo.term,
      total: termo.values.slice(-3).reduce<number>((soma, valor) => soma + (valor ?? 0), 0),
    }))
    .filter((item) => item.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, quantos);

export const RelatorioCompleto = () => {
  const { profileId = "" } = useParams<{ profileId: string }>();
  const [dias, setDias] = useState(30);
  const [todosOsPosts, setTodosOsPosts] = useState(false);
  const { perfil, pesquisas, historico, concorrentes, carregando, carregandoPesquisas, falhou } = useRelatorio(
    profileId,
    dias,
    { historico: true, concorrentes: true },
  );
  const hoje = useMemo(() => new Date(), []);

  const palavras = useMemo(() => [...(perfil?.keywords ?? []), ...(perfil?.extraKeywords ?? [])], [perfil]);
  const posicoes = useMemo(() => resumoDasPosicoes(perfil?.keywords, hoje), [perfil, hoje]);
  const extras = useMemo(() => resumoDasPosicoes(perfil?.extraKeywords, hoje), [perfil, hoje]);
  const medicao = useMemo(() => dataCurta(ultimaMedicao(palavras)), [palavras]);
  const avanco = useMemo(() => maiorAvanco(historico), [historico]);
  const buscas = useMemo(() => (pesquisas && pesquisas.totalByMonth.length ? resumoDasBuscas(pesquisas) : null), [pesquisas]);
  const maisBuscados = useMemo(() => (pesquisas ? termosMaisBuscados(pesquisas) : []), [pesquisas]);
  const posts = useMemo(() => impulsionamentosDoPeriodo(perfil?.posts, dias, hoje), [perfil, dias, hoje]);
  const postsRecentes = useMemo(() => impulsionamentosDoPeriodo(perfil?.posts), [perfil]);
  const respostas = useMemo(() => respostasDoPeriodo(perfil?.reviews, dias, hoje), [perfil, dias, hoje]);
  const trabalho = useMemo(() => linhaDoTempo(perfil?.posts, perfil?.reviews, dias, hoje), [perfil, dias, hoje]);
  const avaliacao = useMemo(() => avaliacaoEmDestaque(perfil?.reviews), [perfil]);
  const respondidas = useMemo(() => (perfil?.reviews ?? []).filter((review) => review.replied).length, [perfil]);

  if (carregando) {
    return (
      <Pagina>
        <Cabecalho />
        <Carregando />
      </Pagina>
    );
  }
  if (falhou || !perfil) {
    return (
      <Pagina>
        <Cabecalho />
        <NaoEncontrado />
      </Pagina>
    );
  }
  if (emPreparacao(perfil)) {
    return (
      <Pagina>
        <Cabecalho />
        <Capa perfil={perfil} />
        <EmPreparacao />
      </Pagina>
    );
  }

  const consultadoEm = dataCurta(hoje);
  const linkGoogle = perfil.searchProfileUrl || perfil.profileUrl;
  // Quatro métricas zeradas no mês não é uma empresa sem movimento, é o Google
  // sem responder (conexão perdida): vira "—", nunca 0.
  const analytics = analyticsValidos(perfil.analytics);
  const rotuloDoPeriodo = PERIODOS.find((periodo) => periodo.dias === dias)?.rotulo ?? `${dias} dias`;
  const fonteDoPeriodo = `Dados do Google · ${rotuloDoPeriodo} · consultado em ${consultadoEm}`;
  const totalDeBuscas = posicoes.total + extras.total;
  const linhasVisiveis = posicoes.linhas.slice(0, 5);
  const temResumo = posicoes.medidas > 0 || analytics !== null;
  const mesesDoPeriodo = Math.max(1, Math.ceil(dias / 30));
  const postsDaFaixa = posts.length > 0 ? posts : postsRecentes.slice(0, 3);
  const postsVisiveis = todosOsPosts ? postsDaFaixa : postsDaFaixa.slice(0, 3);
  const medindo = perfil.rankingSync?.running ? perfil.rankingSync : null;
  const maiorTotal = maisBuscados[0]?.total ?? 0;
  // "À frente" só quando o dado sustenta: aparece em mais buscas do que qualquer concorrente medido.
  const aFrente =
    !!concorrentes &&
    concorrentes.keywordsMeasured > 0 &&
    concorrentes.competitors.length > 0 &&
    concorrentes.competitors.every((empresa) => empresa.appearsIn < concorrentes.profile.appearsIn);

  return (
    <Pagina>
      <Cabecalho acao={linkGoogle ? { href: linkGoogle, rotulo: "Ver minha empresa no Google" } : undefined} />
      <Capa perfil={perfil} />

      <Largura>
        <Secao $atraso={0.2} style={{ paddingBottom: 20 }}>
          <Coluna>
            <CabecaComAcao>
              <Cabeca>
                <Titulo>Resultado do período</Titulo>
                <Apoio>{`O que os clientes fizeram no seu perfil nos últimos ${rotuloDoPeriodo}.`}</Apoio>
              </Cabeca>
              <Chips role="tablist" aria-label="Período">
                {PERIODOS.map((periodo) => (
                  <Chip
                    key={periodo.dias}
                    type="button"
                    role="tab"
                    aria-selected={periodo.dias === dias}
                    $ativo={periodo.dias === dias}
                    onClick={() => setDias(periodo.dias)}
                  >
                    {periodo.rotulo}
                  </Chip>
                ))}
              </Chips>
            </CabecaComAcao>
            <Grade $colunas={2} $desktop={4}>
              {KPIS.map((kpi) => {
                const valor = analytics ? analytics[kpi.chave] : null;
                const variacao = variacaoDoPeriodo(perfil.chartData, kpi.categoria, dias, hoje);
                const selo = seloDeVariacao(variacao, { meses: mesesDoPeriodo });
                return (
                  <Kpi key={kpi.chave} $fundo={valor === null || valor === undefined ? cores.grayTint : kpi.fundo}>
                    <Rotulo>{kpi.rotulo}</Rotulo>
                    <Numero $tamanho={42} $cor={valor === null || valor === undefined ? cores.ink4 : kpi.cor}>
                      {valor === null || valor === undefined ? "—" : <NumeroAnimado valor={valor} atraso={0.3} />}
                    </Numero>
                    {valor === null || valor === undefined ? (
                      <Meta>sem dados neste período</Meta>
                    ) : selo ? (
                      <Meta style={{ color: cores.green, fontFamily: fonts.gilroySemiBold }}>{selo}</Meta>
                    ) : variacao === null ? (
                      <Meta>sem base de comparação</Meta>
                    ) : (
                      <Meta>{`últimos ${rotuloDoPeriodo}`}</Meta>
                    )}
                  </Kpi>
                );
              })}
            </Grade>
            <Meta>{`${fonteDoPeriodo} · variação em meses fechados pelo Google`}</Meta>
            {medindo && (
              <Aviso $cor="azul" role="status" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <span>{`Medindo suas posições agora (${numero(medindo.done)} de ${numero(medindo.total)})`}</span>
                <Barra $largura={medindo.total ? (medindo.done / medindo.total) * 100 : 0} $atraso={0.2} style={{ background: "#ffffff", height: 8, borderRadius: 4 }} />
              </Aviso>
            )}
          </Coluna>
        </Secao>
      </Largura>

      <Largura>
        <Secao $atraso={0.3} style={{ paddingTop: 16 }}>
          <Centro>
            <h1>{temResumo ? "Sua empresa está sendo encontrada no Google" : "Estamos acompanhando sua empresa no Google"}</h1>
            <p className="frase">
              {!temResumo && "Assim que o Google liberar os números do período e as primeiras posições forem medidas, o resumo aparece aqui."}
              {posicoes.top3 > 0 && (
                <>
                  <Destaque $atraso={0.7}>{`${posicoes.top3} de ${posicoes.medidas}`}</Destaque>
                  {" buscas acompanhadas estão entre as 3 primeiras. "}
                </>
              )}
              {posicoes.top3 === 0 && posicoes.melhor && (
                <>
                  {"Sua melhor posição medida é "}
                  <Destaque $atraso={0.7}>{ordinal(posicoes.melhor.posicao)}</Destaque>
                  {` em “${posicoes.melhor.titulo}”. `}
                </>
              )}
              {aFrente && concorrentes && (
                <>
                  {"Nas buscas medidas, você está à frente de "}
                  <Destaque $atraso={0.85}>{`${concorrentes.competitors.length} ${concorrentes.competitors.length === 1 ? "empresa" : "empresas"}`}</Destaque>
                  {" concorrentes. "}
                </>
              )}
              {analytics && (
                <>
                  {`Nos últimos ${rotuloDoPeriodo}: `}
                  <Destaque $cor="verde" $atraso={0.95}>{`${numero(analytics.callClicks ?? 0)} ligações`}</Destaque>
                  {", "}
                  <Destaque $cor="verde" $atraso={1.15}>{`${numero(analytics.requestedRoutes ?? 0)} rotas`}</Destaque>
                  {" e "}
                  <Destaque $cor="verde" $atraso={1.35}>{`${numero(analytics.websiteClicks ?? 0)} acessos`}</Destaque>
                  {" ao site."}
                </>
              )}
            </p>
            {linkGoogle && (
              <LinkExterno href={linkGoogle} target="_blank" rel="noreferrer">
                Ver minha empresa no Google
              </LinkExterno>
            )}
            <Meta>{`Dados do Google · consultado em ${consultadoEm}${medicao ? ` · Medições GeoRanking · ${medicao}` : ""}`}</Meta>
          </Centro>
        </Secao>
      </Largura>

      {posicoes.medidas > 0 && (
        <Largura>
          <Secao $atraso={0.4} style={{ paddingTop: 0, paddingBottom: 8 }}>
            <Bloco>
              <Card style={{ display: "flex", flexDirection: "column", gap: 8, justifyContent: "center" }}>
                <Rotulo>{posicoes.top3 > 0 ? "Buscas acompanhadas" : "Melhor posição medida"}</Rotulo>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                  {posicoes.top3 > 0 ? (
                    <>
                      <Numero $tamanho={64} $cor={cores.blue}>
                        <NumeroAnimado valor={posicoes.top3} atraso={0.4} />
                      </Numero>
                      <span style={{ fontSize: 18, color: cores.ink2 }}>{`de ${posicoes.medidas} entre as 3 primeiras`}</span>
                    </>
                  ) : (
                    <>
                      <Numero $tamanho={64} $cor={cores.blue}>
                        {posicoes.melhor ? ordinal(posicoes.melhor.posicao) : "—"}
                      </Numero>
                      <span style={{ fontSize: 18, color: cores.ink2, overflowWrap: "anywhere" }}>
                        {posicoes.melhor ? `em “${posicoes.melhor.titulo}” · ${posicoes.melhor.onde}` : "sem posição medida"}
                      </span>
                    </>
                  )}
                </div>
                <div style={{ display: "flex", gap: 18, paddingTop: 6, flexWrap: "wrap" }}>
                  <Meta>
                    <strong style={{ color: cores.ink, fontSize: 14 }}>{posicoes.em1}</strong> em 1º
                  </Meta>
                  <Meta>
                    <strong style={{ color: cores.ink, fontSize: 14 }}>{posicoes.top3}</strong> no Top 3
                  </Meta>
                  <Meta>
                    <strong style={{ color: cores.ink, fontSize: 14 }}>{posicoes.top10}</strong> no Top 10
                  </Meta>
                </div>
              </Card>
              {avanco && (
                <Card style={{ display: "flex", flexDirection: "column", gap: 10, justifyContent: "center" }}>
                  <Rotulo style={{ color: cores.green }}>Maior avanço medido</Rotulo>
                  <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 16, lineHeight: 1.35 }}>{avanco.titulo}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Numero $tamanho={36} $cor={cores.ink4}>
                      {ordinal(avanco.antes.posicao)}
                    </Numero>
                    <Seta tamanho={22} />
                    <Numero $tamanho={36} $cor={cores.green}>
                      {ordinal(avanco.depois.posicao)}
                    </Numero>
                  </div>
                  <Meta>{`Google Maps · ${dataCurta(avanco.antes.data) ?? mesCurto(avanco.antes.quando)} → ${dataCurta(avanco.depois.data) ?? mesCurto(avanco.depois.quando)} · Medições GeoRanking`}</Meta>
                </Card>
              )}
            </Bloco>
          </Secao>
        </Largura>
      )}

      {posicoes.total > 0 && (
        <Faixa $cor="azul" $atraso={0.5} style={{ marginTop: 20 }}>
          <Largura>
            <DuasColunas>
              <Coluna>
                <Cabeca>
                  <Titulo>Buscas que trabalhamos para você</Titulo>
                  <Apoio>
                    Acompanhamos estas buscas para saber onde sua empresa aparece no Google Maps e na Pesquisa. Cada posição vem com o lugar e a data da medição.
                  </Apoio>
                </Cabeca>
                {totalDeBuscas > 5 && (
                  <LinkSimples to={`/relatorio/${profileId}/buscas`}>{`Ver todas as ${totalDeBuscas} buscas →`}</LinkSimples>
                )}
              </Coluna>
              <Card style={{ display: "flex", flexDirection: "column", padding: "8px 22px" }}>
                {posicoes.medidas === 0 && (
                  <Aviso $cor="azul" style={{ margin: "14px 0" }}>
                    {medindo
                      ? "Suas buscas estão sendo medidas agora. As posições aparecem aqui assim que a medição terminar."
                      : linhasVisiveis.some((linha) => linha.foraDaLista)
                        ? "Nas últimas medições sua empresa ainda não apareceu na lista destas buscas. Cada uma ganha posição assim que entrar."
                        : "Ainda não há posição medida para estas buscas. Cada uma ganha lugar e data assim que for medida."}
                  </Aviso>
                )}
                {linhasVisiveis.map((linha) => (
                  <LinhaDeBuscaItem key={linha.id} linha={linha} />
                ))}
              </Card>
            </DuasColunas>
          </Largura>
        </Faixa>
      )}

      <Largura>
        <Secao $atraso={0.6}>
          <DuasColunas>
            <Coluna>
              <Cabeca>
                <Titulo>Como encontraram sua empresa</Titulo>
                <Apoio>Pesquisas reais que fizeram sua empresa aparecer no Google — não é o total de buscas da cidade.</Apoio>
              </Cabeca>
              {buscas && buscas.totalRecente > 0 ? (
                <>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                    <Numero $tamanho={56}>
                      <NumeroAnimado valor={buscas.totalRecente} atraso={0.3} />
                    </Numero>
                    <span style={{ fontSize: 18, color: cores.ink2 }}>pesquisas registradas</span>
                  </div>
                  {buscas.percentualPorNome !== null && (
                    <Coluna $gap={3}>
                      <span style={{ fontSize: 15 }}>
                        <strong style={{ fontFamily: fonts.gilroyBold }}>{`${100 - buscas.percentualPorNome}%`}</strong> das pesquisas não citavam o nome da empresa
                      </span>
                      <Meta>{`${buscas.percentualPorNome}% pesquisaram diretamente pelo nome · ${buscas.periodoRecente}, meses fechados pelo Google`}</Meta>
                    </Coluna>
                  )}
                  <LinkSimples to={`/relatorio/${profileId}/pesquisas`}>Ver análise completa →</LinkSimples>
                </>
              ) : carregandoPesquisas ? (
                <Meta>Carregando as pesquisas do Google…</Meta>
              ) : (
                <Card $fundo={cores.grayTint} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <Apoio style={{ margin: 0 }}>Ainda não há meses fechados suficientes para mostrar as pesquisas deste perfil.</Apoio>
                  <Etiqueta $cor="amarela" style={{ alignSelf: "flex-start" }}>{`${mesCurto(`${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`)} ainda não liberado pelo Google`}</Etiqueta>
                </Card>
              )}
            </Coluna>
            {maisBuscados.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {maisBuscados.map((item, indice) => (
                  <div key={item.termo} style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 0", borderTop: `1px solid ${cores.line}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                      <span style={{ fontSize: 15, overflowWrap: "anywhere" }}>{item.termo}</span>
                      <Numero $tamanho={17}>{numero(item.total)}</Numero>
                    </div>
                    <Barra $largura={(item.total / maiorTotal) * 100} $atraso={0.5 + indice * 0.15} />
                  </div>
                ))}
              </div>
            )}
          </DuasColunas>
        </Secao>
      </Largura>

      {concorrentes && concorrentes.keywordsMeasured > 0 && concorrentes.competitors.length > 0 && (
        <Faixa $cor="verde" $atraso={0.65}>
          <Largura>
            <DuasColunas>
              <Coluna>
                <Cabeca>
                  <Titulo>{aFrente ? "Você está à frente da concorrência" : "Seu mercado no Google"}</Titulo>
                  <Apoio>
                    {aFrente
                      ? `Nas ${concorrentes.keywordsMeasured} buscas medidas, sua empresa aparece em ${concorrentes.profile.appearsIn} — mais do que qualquer outra empresa da lista. É o espaço que o trabalho contínuo mantém ocupado. Dados públicos do Google Maps, última medição de cada busca.`
                      : "Empresas que aparecem nas mesmas buscas que você no Google Maps. Somente dados públicos da lista de resultados, da última medição de cada busca."}
                  </Apoio>
                </Cabeca>
              </Coluna>
              <Card style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <span style={{ fontSize: 15 }}>
                  Você aparece em <strong style={{ fontFamily: fonts.gilroyBold }}>{`${concorrentes.profile.appearsIn} de ${concorrentes.keywordsMeasured}`}</strong> buscas medidas
                  {concorrentes.profile.top3 > 0 && (
                    <>
                      {" · "}
                      <strong style={{ fontFamily: fonts.gilroyBold }}>{concorrentes.profile.top3}</strong> entre as 3 primeiras
                    </>
                  )}
                </span>
                <Coluna $gap={13}>
                  <LinhaDeMercado nome="Sua empresa" valor={concorrentes.profile.appearsIn} total={concorrentes.keywordsMeasured} destaque atraso={0.5} />
                  {concorrentes.competitors.slice(0, 4).map((empresa, indice) => (
                    <LinhaDeMercado key={empresa.placeId ?? empresa.name} nome={empresa.name} valor={empresa.appearsIn} total={concorrentes.keywordsMeasured} atraso={0.65 + indice * 0.15} />
                  ))}
                </Coluna>
                <Meta>{`Medições GeoRanking · ${dataCurta(concorrentes.measuredAt) ?? ""} · ${concorrentes.surface}`}</Meta>
              </Card>
            </DuasColunas>
          </Largura>
        </Faixa>
      )}

      {trabalho.length > 0 && (
        <Largura>
          <Secao $atraso={0.7} style={{ paddingTop: 0 }}>
            <DuasColunas $proporcao="1fr 1fr">
              <Coluna $gap={20}>
                <Cabeca>
                  <Titulo>O trabalho por trás desse resultado</Titulo>
                  <Apoio>{`O que a GeoRanking fez no seu perfil nos últimos ${rotuloDoPeriodo} — resultado no Google é consequência de trabalho contínuo.`}</Apoio>
                </Cabeca>
                <div style={{ display: "flex", gap: 12 }}>
                  <Coluna $gap={2} style={{ flex: 1 }}>
                    <Numero $tamanho={32}>
                      <NumeroAnimado valor={posts.length} />
                    </Numero>
                    <Meta>{posts.length === 1 ? "impulsionamento" : "impulsionamentos"}</Meta>
                  </Coluna>
                  <Coluna $gap={2} style={{ flex: 1 }}>
                    <Numero $tamanho={32}>
                      <NumeroAnimado valor={respostas.length} />
                    </Numero>
                    <Meta>{respostas.length === 1 ? "avaliação respondida" : "avaliações respondidas"}</Meta>
                  </Coluna>
                </div>
              </Coluna>
              <div style={{ display: "flex", flexDirection: "column" }}>
                {trabalho.map((item) => (
                  <div key={item.chave} style={{ display: "flex", gap: 14, padding: "14px 0", borderTop: `1px solid ${cores.line}` }}>
                    <div style={{ width: 8, height: 8, borderRadius: 999, background: item.tipo === "impulsionamento" ? cores.blue : cores.green, marginTop: 6, flexShrink: 0 }} />
                    <Coluna $gap={2}>
                      <Meta style={{ fontFamily: fonts.gilroySemiBold }}>{`${item.data} · ${item.rotulo}`}</Meta>
                      <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 15, lineHeight: 1.35 }}>{item.titulo}</span>
                    </Coluna>
                  </div>
                ))}
              </div>
            </DuasColunas>
          </Secao>
        </Largura>
      )}

      {avaliacao && (
        <Faixa $cor="amarela" $atraso={0.8}>
          <Largura>
            <DuasColunas>
              <Coluna>
                <Cabeca>
                  <Titulo>Avaliações</Titulo>
                  <Apoio>
                    {respondidas > 0 ? (
                      <>
                        <strong style={{ color: cores.ink, fontFamily: fonts.gilroyBold }}>{respondidas}</strong>
                        {respondidas === 1 ? " avaliação respondida pela GeoRanking." : " avaliações respondidas pela GeoRanking."}
                      </>
                    ) : (
                      "A avaliação mais recente do seu perfil no Google."
                    )}
                  </Apoio>
                </Cabeca>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  {linkGoogle && (
                    <LinkExterno href={linkGoogle} target="_blank" rel="noreferrer">
                      Ver avaliações
                    </LinkExterno>
                  )}
                  {perfil.reviewUrl && (
                    <LinkExterno href={perfil.reviewUrl} target="_blank" rel="noreferrer">
                      Pedir avaliação
                    </LinkExterno>
                  )}
                </div>
              </Coluna>
              <Card style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 999, background: cores.blueTint, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: fonts.gilroySemiBold, fontSize: 14, color: cores.blue, flexShrink: 0 }}>
                    {(avaliacao.userName || "?")[0]?.toUpperCase()}
                  </div>
                  <Coluna $gap={1} style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 15, fontFamily: fonts.gilroySemiBold }}>{avaliacao.userName || "Cliente"}</span>
                    <Meta>{`avaliação no Google · ${dataCurta(avaliacao.createTime) ?? ""}`}</Meta>
                  </Coluna>
                  <span aria-label={`${estrelas(avaliacao.starRating)} estrelas`} style={{ color: "#F9AB00", fontSize: 13, letterSpacing: 1, flexShrink: 0 }}>
                    {"★".repeat(estrelas(avaliacao.starRating))}
                  </span>
                </div>
                {avaliacao.comment && <p style={{ fontSize: 15, lineHeight: 1.55, margin: 0 }}>{`“${avaliacao.comment}”`}</p>}
                {avaliacao.replied && avaliacao.reply && (
                  <div style={{ background: cores.grayTint, borderRadius: 16, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 3 }}>
                    <Rotulo>{`Resposta da empresa${avaliacao.repliedAt ? ` · ${dataCurta(avaliacao.repliedAt)}` : ""}`}</Rotulo>
                    <Apoio style={{ margin: 0, fontSize: 14 }}>{`“${avaliacao.reply}”`}</Apoio>
                  </div>
                )}
              </Card>
            </DuasColunas>
          </Largura>
        </Faixa>
      )}

      {postsDaFaixa.length > 0 && (
        <Faixa $cor="escura" $atraso={0.9}>
          <Largura>
            <Coluna $gap={18}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                <Cabeca>
                  <Titulo style={{ color: "#ffffff" }}>Impulsionamentos</Titulo>
                  <Apoio $claro>
                    {posts.length > 0
                      ? "Publicações que fizemos no seu perfil para manter a empresa ativa e em destaque no Google — sem intervalo."
                      : `Nenhum impulsionamento nos últimos ${rotuloDoPeriodo}. Estes são os últimos publicados no seu perfil.`}
                  </Apoio>
                </Cabeca>
                <Etiqueta $cor="clara" style={{ flexShrink: 0 }}>
                  {posts.length > 0 ? `${posts.length} no período` : `${postsRecentes.length} no último ano`}
                </Etiqueta>
              </div>
              <Grade $colunas={1} $desktop={3}>
                {postsVisiveis.map((post) => (
                  <Card
                    key={`${post.createTime}-${post.summary.slice(0, 20)}`}
                    as={post.searchUrl ? "a" : "div"}
                    href={post.searchUrl || undefined}
                    target={post.searchUrl ? "_blank" : undefined}
                    rel={post.searchUrl ? "noreferrer" : undefined}
                    style={{ display: "flex", alignItems: "center", gap: 14, padding: 14, color: cores.ink }}
                  >
                    <Imagem
                      candidatas={post.mediaUrl ?? []}
                      largura={320}
                      style={{ width: 72, height: 60, borderRadius: 14, flexShrink: 0, objectFit: "cover", display: "block" }}
                      reserva={<div style={{ width: 72, height: 60, borderRadius: 14, flexShrink: 0, background: "linear-gradient(160deg,#9DB7EA,#5E86D6)" }} />}
                    />
                    <Coluna $gap={3} style={{ minWidth: 0 }}>
                      <span style={{ fontFamily: fonts.gilroySemiBold, fontSize: 14, lineHeight: 1.35 }}>{primeiraFrase(post.summary || "Impulsionamento no seu perfil")}</span>
                      <Meta>{`impulsionamento · ${dataCurta(post.createTime) ?? ""}`}</Meta>
                    </Coluna>
                  </Card>
                ))}
              </Grade>
              {postsDaFaixa.length > 3 && (
                <LinkExterno as="button" type="button" onClick={() => setTodosOsPosts((valor) => !valor)} style={{ background: "none", border: 0, padding: 0, cursor: "pointer", color: "#8AB4F8", textAlign: "left" }}>
                  {todosOsPosts ? "Mostrar menos" : `Ver todos os ${postsDaFaixa.length} impulsionamentos →`}
                </LinkExterno>
              )}
            </Coluna>
          </Largura>
        </Faixa>
      )}

      <Pe texto={`Relatório GeoRanking · Dados do Google e medições GeoRanking · ${consultadoEm}`} />
    </Pagina>
  );
};
